import { instance } from '@/services/api/index';
import { useAuthStore } from '@/stores/auth';
import { useOrderStore } from '@/stores/orders';

let pollingInterval = null;
let isPollingActive = false;
let reconnectTimer = null;
let consecutiveNoChanges = 0;

const POLL_INTERVAL_FAST = 1500;   // 1.5s bei aktiven Bestellungen
const POLL_INTERVAL_NORMAL = 3000; // 3s Standard
const POLL_INTERVAL_SLOW = 6000;   // 6s wenn lange keine Änderungen
const NO_CHANGE_THRESHOLD = 20;    // Nach 20 gleichen Polls -> slow mode

const getCurrentInterval = () => {
  if (consecutiveNoChanges > NO_CHANGE_THRESHOLD) return POLL_INTERVAL_SLOW;
  if (consecutiveNoChanges > 3) return POLL_INTERVAL_NORMAL;
  return POLL_INTERVAL_FAST;
};

export const connectToKitchenHub = async () => {
  const authStore = useAuthStore();
  const ordersStore = useOrderStore();

  if (!authStore.isAuthenticated || !authStore.token) {
    console.warn("[Kitchen] Nicht authentifiziert. Polling wird nicht gestartet.");
    ordersStore.setKitchenConnectionStatus('disconnected');
    return;
  }

  if (isPollingActive) return;

  ordersStore.setKitchenConnectionStatus('connecting');
  startPolling(ordersStore);
  ordersStore.setKitchenConnectionStatus('connected');
  return null;
};

const startPolling = (ordersStore) => {
  stopPolling();
  isPollingActive = true;
  consecutiveNoChanges = 0;

  const poll = async () => {
    if (!isPollingActive) return;
    try {
      const previousOrders = JSON.stringify(ordersStore.orders?.map(o => `${o.id}:${o.status}`) || []);
      await ordersStore.fetchKitchenOrders();
      const currentOrders = JSON.stringify(ordersStore.orders?.map(o => `${o.id}:${o.status}`) || []);

      if (previousOrders === currentOrders) {
        consecutiveNoChanges++;
      } else {
        consecutiveNoChanges = 0;
      }
    } catch (err) {
      console.error("[Kitchen] Polling-Fehler:", err);
    }
    if (isPollingActive) {
      pollingInterval = setTimeout(poll, getCurrentInterval());
    }
  };

  poll();
};

const stopPolling = () => {
  if (pollingInterval) {
    clearTimeout(pollingInterval);
    pollingInterval = null;
  }
  isPollingActive = false;
  consecutiveNoChanges = 0;
};

export const disconnectFromKitchenHub = () => {
  stopPolling();
  const ordersStore = useOrderStore();
  ordersStore.setKitchenConnectionStatus('disconnected');
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
};

export let kitchenHubConnection = null;
export const getKitchenHubConnection = () => null;
export const isKitchenHubConnected = () => isPollingActive;
export const isPollingFallbackActive = () => isPollingActive;

export const connectToOrderHub = async () => null;
export const disconnectFromOrderHub = () => {};

export const joinTableGroup = async () => {
  consecutiveNoChanges = 0;
};

export const leaveTableGroup = async () => {};

export const orderService = {
  getSessionOrders(sessionId) {
    if (!sessionId) {
      return Promise.reject(new Error('Session ID ist erforderlich'));
    }

    return instance.get(`/orders/session/${sessionId}?includeItems=true`)
      .then(response => {
        let orders = [];
        if (Array.isArray(response.data)) {
          orders = response.data;
        } else if (response.data && typeof response.data === 'object') {
          if (response.data.id) {
            orders = [response.data];
          }
        }
        return { ...response, data: orders };
      })
      .catch(error => {
        if (error.response && error.response.status === 404) {
          return { data: [] };
        }
        throw error;
      });
  },

  getTableInfo(tableId) {
    return instance.get(`/tables/${tableId}`);
  },

  getSessionInfo(sessionId) {
    return instance.get(`/OrderSessions/identifier/${sessionId}`);
  },

  requestBill(sessionId) {
    return instance.post(`/OrderSessions/${sessionId}/request-bill`);
  },

  requestPaymentBySession(sessionId) {
    return instance.post(`/OrderSessions/${sessionId}/request-payment`);
  },

  getSessionPaymentStatus(sessionId) {
    return instance.get(`/OrderSessions/${sessionId}/payment-status`);
  },

  async createOrder(orderData, tableId = null) {
    if (!orderData.items || orderData.items.length === 0) {
      throw new Error('Bestellung muss mindestens ein Artikel enthalten');
    }

    let payload = {
      ...orderData,
      items: orderData.items.map(item => ({
        productId: item.productId,
        quantity: item.quantity,
        extras: item.extras || (item.extraOptionIds || []).map(id => ({ id })),
        specialInstructions: item.specialInstructions || ''
      }))
    };

    if (tableId) {
      payload.tableId = tableId;
    }

    return instance.post(`/orders`, payload);
  },

  async placeOrder(orderData) {
    let normalizedData = { ...orderData };

    if (normalizedData.items && Array.isArray(normalizedData.items)) {
      normalizedData.items = normalizedData.items.map(item => {
        const normalizedItem = { ...item };
        if (normalizedItem.options && !normalizedItem.extraOptionIds) {
          normalizedItem.extras = normalizedItem.options.map(opt =>
            typeof opt === 'object' ? opt : { id: opt }
          );
          delete normalizedItem.options;
        }
        return normalizedItem;
      });
    }

    if (normalizedData.paymentMethod === 'Cash' || normalizedData.paymentMethod === 'cash') {
      normalizedData.paymentStatus = 'Paid';
    }

    return instance.post('/orders', normalizedData);
  },

  updatePaymentStatus(orderId, paymentStatus, paymentMethod = 'Stripe') {
    const data = { paymentStatus, paymentMethod };
    if (paymentMethod === 'Cash' && paymentStatus === 'Paid') {
      data.isPaid = true;
    }
    return instance.put(`/orders/${orderId}/payment-status`, data);
  },

  async updateOrderStatus(orderId, status) {
    const statusValue = typeof status === 'string' ? status : (status?.status || null);
    if (!statusValue) {
      return Promise.reject(new Error('Status muss angegeben werden'));
    }
    return instance.put(`/orders/${orderId}/status`, JSON.stringify(statusValue), {
      headers: { 'Content-Type': 'application/json' }
    });
  },

  getOrders() {
    return instance.get(`/orders`);
  },

  getOrdersByTable(tableId, params = {}) {
    if (!tableId || tableId <= 0) {
      return Promise.reject(new Error('TableId muss größer als 0 sein'));
    }
    return instance.get(`/orders/table/${tableId}`, { params });
  },

  getOrderById(orderId) {
    return instance.get(`/orders/${orderId}?includeItems=true`);
  },

  markOrderAsPaid(orderId, paymentMethod = 'Stripe') {
    return this.updatePaymentStatus(orderId, 'Paid', paymentMethod);
  },

  verifyPayment(orderId, stripeSessionId) {
    return instance.post(`/payments/${orderId}/verify-payment`, JSON.stringify(stripeSessionId), {
      headers: { 'Content-Type': 'application/json' }
    }).catch(() => {
      return instance.post(`/stripe/verify-payment`, {
        orderId,
        sessionId: stripeSessionId
      });
    });
  },

  async extendOrder(orderId, newItems) {
    const response = await instance.post(`/orders/${orderId}/extend`, { newItems });
    return response.data;
  },

  getOrdersAdmin() {
    return instance.get(`/orders`, { headers: getAuthHeader() });
  },

  updateOrderStatusAdmin(orderId, status) {
    return instance.patch(`/orders/${orderId}/status`, status, {
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() }
    });
  }
};

const getAuthHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem('auth_token')}`
});
